import { Injectable } from "@angular/core";
import { RemoteGatewayFactory } from "../../gateways/remote-gateway-factory";
import { PageableResult } from "../../models/domains/paginator/pageable-result";
import { AddClusterProduct } from "../../models/domains/pbm/clusters/add-cluster-product.model";
import { ClusterSearchParameters } from "../../models/domains/pbm/clusters/cluster-search-parameters.model";
import { Cluster } from "../../models/domains/pbm/clusters/cluster.model";
import { FileCluster } from "../../models/domains/pbm/clusters/file-cluster";
import { ImportClusterResult } from "../../models/domains/pbm/clusters/import-cluster-result";
import { ExportOrderKey } from "../../models/domains/pbm/exports/export-order-key";
import { BackendUrl } from "../../models/domains/urls/back-end.url";
import { AddNewClusterParser } from "../parsers/pbm/clusters/add-new-cluster.parser";
import { ClusterParser } from "../parsers/pbm/clusters/cluster.parser";
import { ClustersPageableResultParser } from "../parsers/pbm/clusters/clusters-pageable-result.parser";
import { ClusterProductsPageableResultParser } from "../parsers/pbm/clusters/clusters-products-pageable-result.parser";
import { ImportFileClusterParser } from "../parsers/pbm/clusters/import-cluster-file.parser";

@Injectable()
export class ClustersRepository {
  constructor(
    private remoteGatewayFactory: RemoteGatewayFactory,
    private importFileClusterParser: ImportFileClusterParser,
    private clustersPageableResultParser: ClustersPageableResultParser,
    private clusterParser: ClusterParser,
    private clustersProductsPageableResultParser: ClusterProductsPageableResultParser,
    private addNewClusterParser: AddNewClusterParser,
  ) {}

  public async importCluster(fileCluster: FileCluster): Promise<ImportClusterResult> {
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithFile(new BackendUrl("/api/cluster/import/products/cluster/" + fileCluster.getClusterId()), fileCluster.getFile());

      return await Promise.resolve(this.importFileClusterParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async exportCluster(cluster: Cluster): Promise<ExportOrderKey> {
    try {
      const headers = { "Content-Type": "application/json" };
      const body = JSON.stringify({
        IdCluster: cluster.getClusterKey().getValue(),
        Filter: "",
      });
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .postWithHeaders(new BackendUrl("/api/cluster/export/products-cluster"), body, headers);

      const exportOrderKey = new ExportOrderKey(data.toString());
      return exportOrderKey;
    } catch (httpErrorResponse) {
      if (httpErrorResponse.error) {
        return await Promise.reject(httpErrorResponse.error.Data.ErrorMessage);
      } else if (httpErrorResponse.status == 404) {
        return await Promise.reject("Cluster não encontrado");
      }
    }
  }

  public async findClusterExportOrderKey(request: ExportOrderKey): Promise<ExportOrderKey> {
    const headers = { "Content-Type": "application/json" };
    try {
      const data = await this.remoteGatewayFactory
        .createDefaultRemoteGateway()
        .getWithHeaders(new BackendUrl("/api/export/" + request.getValue()), headers);
      const exportOrderKey = new ExportOrderKey(data.toString());

      return exportOrderKey;
    } catch (referenceError) {
      const errorMessage = referenceError; //.error.Data.ErrorMessage;
      return await Promise.reject(errorMessage);
    }
  }

  public async getClustersPageableResult(clustersSearchParameters: ClusterSearchParameters): Promise<PageableResult> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify(clustersSearchParameters);

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new BackendUrl("/api/cluster/clusters"), body, headers);
      return await Promise.resolve(this.clustersPageableResultParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getCluster(clusterKey: string): Promise<Cluster> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.get(new BackendUrl(`/api/cluster?id=${clusterKey}`));
      return await Promise.resolve(this.clusterParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async addNewCluster(cluster: Cluster): Promise<void> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      ClusterKey: {
        Value: cluster.getClusterKey(),
      },
      Name: cluster.getName(),
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new BackendUrl("/api/cluster"), body, headers);
      return await Promise.resolve(this.addNewClusterParser.parse(data));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async addClusterProduct(clusterProduct: AddClusterProduct): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      IdCluster: clusterProduct.getClusterId(),
      ListEanProducts: [clusterProduct.getEan()],
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new BackendUrl("/api/cluster/add-products-cluster/"), body, headers);
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error.error.Data.ErrorMessage);
    }
  }

  public async deleteClusterProduct(clusterKey: string, ean: string): Promise<any> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.delete(new BackendUrl(`/api/cluster/remove-product-cluster?IdCluster=${clusterKey}&EanProduct=${ean}`));
      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getAllClusters(): Promise<any> {
    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.getWithHeaders(new BackendUrl("/api/cluster/all"), {});

      return await Promise.resolve(data);
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async getClusterProducts(clusterKey: string, clusterSearchParameters: ClusterSearchParameters): Promise<any> {
    const headers = {
      "Content-Type": "application/json",
    };

    const body = JSON.stringify({
      IdCluster: clusterKey,
      FilterText: clusterSearchParameters.getFilterText(),
      StartIndex: clusterSearchParameters.getStartIndex(),
      TotalPerPages: clusterSearchParameters.getTotalPerPages(),
    });

    try {
      const remoteGateway = this.remoteGatewayFactory.createDefaultRemoteGateway();
      const data = await remoteGateway.postWithHeaders(new BackendUrl("/api/cluster/get-products-cluster/"), body, headers);

      return await Promise.resolve(this.clustersProductsPageableResultParser.parse(data));
    } catch (error) {
      return await Promise.reject(error.error.Data.ErrorMessage);
    }
  }
}
